import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject, Subject, catchError, filter, map, mergeMap, of, share, shareReplay, switchMap, tap } from 'rxjs';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import * as _ from 'lodash';
import { ServerMsg } from '@app/models/server-msg';
import { ServerMessageType } from '@app/enums/server-message-type';
import { ServerResponse } from '@app/enums/server-response';
import { ServerRequestMessage } from '@app/enums/server-request';
import { ConnectionStatus } from '@app/enums/connection-status';

import { Store, select } from '@ngrx/store';

import * as WSActions from '@app/store/features/web-socket/web-socket.actions';
import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import { environment } from '@environments/environment';
import Bowser from 'bowser';

@Injectable({
  providedIn: 'root'
})
export class WsService {

  private readonly _store: Store<any> = inject(Store);

  ws: WebSocketSubject<any>;
  wsConnection: Observable<any>
  //stream = new ReplaySubject<any>()
  stream = new Subject<any>()

  constructor() {
  }

  connect(url: string, token: string, skinName: string) {
    // console.log("@WS Connect 🌱")
    this.ws = webSocket({
      url, //: 'wss://testgame.theonlypoker.com:14445',
      openObserver: {
        next: () => {
          this._store.dispatch(WSActions.updateConnectionStatus({ status: ConnectionStatus.Connected }))

          const sendAuth = (guid: string) => {
            // console.log("🟢", "Connected");
            this.ws.next({
              type: 6, // ClientMsgType.ConnectWithAuthToken = 6
              Str1: token, // 
              Str2: `HTML-v4.1`, //ClientName
              Str3: skinName,
              Str4: this.getBrowserData(), // OsVersion
              Str5: guid, // Unique guid to identify the device (can be generated locally) this will let connection overtake existing one.
              Value2: 12 // platform (1 for Windows, 2: HTML, 3:Android, 4:iOS, 7:Mac, 8:Bot)
            })
          }



          /*
 this.service.sendData({
          type: 3,
          Str1: `Angular`, // Name
          Str2: this.configService.config.skinName, // Skin/ReferralName
          Str3: 'v2.0', // Client Version
          Str4: this.getBrowserData(), // OsVersion
          Str5: guid, // Unique guid to identify the device (can be generated locally) this will let connection overtake existing one.
          Value2: 2 // platform (1 for Windows, 2: HTML, 3:Android, 4:iOS, 7:Mac, 8:Bot)
        } as ClientInfo)
          */
          const guid = localStorage.getItem(`guid`) ?? null

          if (guid) {
            sendAuth(guid)
          } else if (environment.production) {
            FingerprintJS.load({ token: 'LKv84d9KPsyj2Kv4juhs' })
              .then(fp => fp.get())
              .then(result => sendAuth(result.visitorId))
              .catch(() => sendAuth(`GUID_ERR_${Math.floor(Math.random() * 1000000)}_AUTO_ID`))
          } else {
            sendAuth(`AUTO_ID_${Math.floor(Math.random() * 1000000)}_TEST`)
          }
        }
      },
      closeObserver: {
        next: () => {

          // console.log("🔴", "Disconnected")
        }
      },
      closingObserver: {
        next: () => {
          this._store.dispatch(WSActions.updateConnectionStatus({ status: ConnectionStatus.Closed }))
          // console.log("🔴", "Closing")
        }
      },
      deserializer: (event) => {
        return this.format(JSON.parse(event.data), 'camelCase')
      },
      serializer: (value) => {
        return JSON.stringify(this.format(value, 'pascalCase'))
      }
    })

    // setTimeout(() => {
    //   // Simulate a runtime socket failure after 2s
    //   this.ws.error(new Error('💥 Simulated WebSocket error'));
    //   console.warn('WebSocket error simulated');
    // }, 12000);


    return this.ws
      .asObservable()
      .pipe(
        //tap(message => console.log('🟠', message)),
        tap(message => this.stream.next(message)),
        catchError(err => {
          console.warn('WebSocket error suppressed:', err); // Optional: remove this line to fully suppress
          this._store.dispatch(WSActions.updateConnectionStatus({ status: ConnectionStatus.Disconnected }))
          // TO DO implementation of Disconnected status
          return of(); // Complete the observable silently
        })
      )




  }




  format(data: any, type: 'camelCase' | 'pascalCase'): any {
    if (_.isArray(data)) {
      return data.map((item: any) => this.format(item, type));
    } else if (_.isObject(data) && data !== null) {
      const convertedObj: any = {};
      _.mapKeys(data, (value: any, key: any) => {
        const newKey = type === 'camelCase' ? _.camelCase(key) : _.upperFirst(_.camelCase(key));
        convertedObj[newKey] = this.format(value, type);
      });
      return convertedObj;
    }

    if (typeof data === 'string') {
      if (data === 'False' || data === 'false') {
        return false;
      } else if (data === 'True' || data === 'true') {
        return true;
      }
    }
    return data;
  }


  sendData(data: any): void {
    this.ws.next(data)
  }

  getDataResponse<T>(serverResponse: ServerResponse): Observable<T> {



    return this.stream
      .pipe(
        filter(data => data.response === serverResponse),
        map(data => data[_.camelCase(serverResponse)] as T)
      )
  }

  getServerMsg<T>(serverMessageType: ServerMessageType): Observable<T> {
    return this.stream
      .pipe(
        // tap(data => console.log('🟢', data)),

        filter(data => data.response === 'ServerMsg'),
        // tap(data => console.log('ServerMsg 🟢', data, serverMessageType)),

        filter(data => (data.serverMsg as ServerMsg[]).filter(serverMsg => serverMsg.msgType === serverMessageType).length > 0),
        map(data => (data.serverMsg as ServerMsg[]).filter(serverMsg => serverMsg.msgType === serverMessageType)),
        mergeMap(data => data as T[]),
        // tap(data => console.log('2 🟢', data))
        // shareReplay({ bufferSize: 1, refCount: true })
        share()
      )
  }

  disconnect(): void {
    this.ws.complete()
  }

  sendPing() {
    this.sendData({
      type: ServerRequestMessage.Ping,
      Value2: new Date().getTime() + Math.random()
    });
  }

  private getBrowserData(): string {
    const { browser, os } = Bowser.parse(window.navigator.userAgent);
    return `${browser.name ?? 'Unknown'} ${browser.version ?? ''} / ${os.name ?? 'Unknown'} ${os.version ?? ''}`.trim();
  }
}
